"use client";

import React from "react";
import { Calendar, User, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="221775" className="mx-auto max-w-4xl animate-pulse px-4">
        <div className="mb-8 flex justify-center gap-2">
          <div className="h-6 w-24 rounded-full bg-muted"></div>
        </div>
        <div className="mb-6 h-12 w-3/4 mx-auto rounded-md bg-muted"></div>
        <div className="mb-12 flex justify-center gap-4">
          <div className="h-5 w-32 rounded-md bg-muted"></div>
          <div className="h-5 w-32 rounded-md bg-muted"></div>
        </div>
        <div className="mb-12 aspect-video w-full rounded-2xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-5/6 rounded bg-muted"></div>
          <div className="h-4 w-4/6 rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center">
        <h2 className="font-default text-2xl font-semibold text-destructive mb-4">Error Loading Guide</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center">
        <h1 className="font-default text-4xl font-bold text-foreground mb-4">Guide Not Found</h1>
        <p className="text-muted-foreground mb-8">The smart home tutorial you are looking for is currently unavailable.</p>
        <Link className="inline-flex h-10 items-center justify-center rounded-full bg-primary px-8 text-sm font-medium text-primary-foreground shadow-lg transition-colors hover:bg-primary/90" to="#">
          Back to Home
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-4">
      <header className="mb-10 text-center md:mb-16">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            {post.categories.map((category, i) => (
              <Link
                key={category.id}
                data-index={i}
                to={category.link || "#"}
                className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 uppercase tracking-widest"
              >
                <Tag className="size-3" />
                {category.name}
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="font-default text-4xl font-bold leading-tight tracking-tighter text-foreground md:text-5xl lg:text-6xl mb-6">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground font-mono">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12 overflow-hidden rounded-3xl border border-border shadow-xl md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
          />
        </figure>
      )}

      <div 
        className="font-serif text-xl leading-relaxed text-foreground/90 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:tracking-tight [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-4 [&>p]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-2 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:mb-6 [&>img]:rounded-2xl [&>img]:my-8 [&>img]:shadow-md"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      <Separator className="my-12 md:my-16 bg-border/50" />

      {post.author && (
        <section className="rounded-3xl border border-border bg-accent/30 p-6 shadow-sm md:p-8">
          <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <Avatar className="size-20 border border-border sm:size-24">
              <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
              <AvatarFallback className="font-default text-2xl font-bold bg-secondary text-secondary-foreground">
                {post.author.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-default text-xl font-bold text-foreground mb-2">
                {post.author.name}
              </h3>
              {post.author.slug && (
                <p className="text-muted-foreground leading-relaxed font-sans text-base">
                  Smart home enthusiast and Apple HomeKit specialist at moza casa. Dedicated to simplifying modern living through elegant automation and intuitive technology.
                </p>
              )}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}